/**
 * FDM Flights Tab Component
 * Shows flight records with filtering and detail view
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plane, Eye, RefreshCw, ChevronRight, PlaneLanding, PlaneTakeoff 
} from 'lucide-react';

export default function FDMFlightsTab({ 
  flights, 
  flightsTotal,
  flightFilters,
  setFlightFilters,
  aircraftTypes,
  onFlightClick
}) {
  return (
    <div className="space-y-4 mt-6">
      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Aircraft Type</Label>
              <Select 
                value={flightFilters.aircraft_type || 'all'} 
                onValueChange={(v) => setFlightFilters({...flightFilters, aircraft_type: v === 'all' ? undefined : v})}
              >
                <SelectTrigger className="w-[140px] bg-slate-900/50">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {aircraftTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Has Exceedances</Label>
              <Select 
                value={flightFilters.has_exceedances === true ? 'yes' : flightFilters.has_exceedances === false ? 'no' : 'any'} 
                onValueChange={(v) => setFlightFilters({...flightFilters, has_exceedances: v === 'yes' ? true : v === 'no' ? false : undefined})}
              >
                <SelectTrigger className="w-[140px] bg-slate-900/50">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="yes">With Exceedances</SelectItem>
                  <SelectItem value="no">Clean Flights</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setFlightFilters({})}>
              <RefreshCw className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Flights Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg text-slate-100">Flight Records</CardTitle>
          <CardDescription>{flightsTotal} total flights</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {flights.map((flight) => (
              <div 
                key={flight.id} 
                className="flex items-center justify-between p-4 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors cursor-pointer" 
                onClick={() => onFlightClick(flight)}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <Plane className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-200">{flight.flight_info?.flight_number}</span>
                      <Badge variant="outline" className="text-xs">{flight.flight_info?.aircraft_registration}</Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-400 mt-1">
                      <PlaneTakeoff className="w-3 h-3" />
                      <span>{flight.flight_info?.departure_airport}</span>
                      <ChevronRight className="w-3 h-3" />
                      <PlaneLanding className="w-3 h-3" />
                      <span>{flight.flight_info?.arrival_airport}</span>
                      <span className="text-slate-600">•</span>
                      <span>{flight.flight_info?.aircraft_type}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {flight.exceedances_count > 0 ? (
                    <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                      {flight.exceedances_count} exceedance{flight.exceedances_count > 1 ? 's' : ''}
                    </Badge>
                  ) : (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      Clean
                    </Badge>
                  )}
                  <Eye className="w-4 h-4 text-slate-500" />
                </div>
              </div>
            ))}
            {flights.length === 0 && (
              <div className="text-center py-12">
                <Plane className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No flight records found</p>
                <p className="text-slate-500 text-sm mt-1">Upload flight data or seed demo data to get started</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
